import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError, upload } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { MessageKey } from "../lib/messages";
import { cityName } from "../lib/saudi";
import { useSession } from "../lib/session";
import { useAsync } from "../lib/useAsync";
import { IconAlert, IconCamera, IconCheck, IconScan, IconShield, IconTruck, IconX } from "../ui/icons";
import { DeviceStep, findModel, norm, storageGb, storageLabel, useDeviceLibrary } from "./SellDevice";
import { AsyncButton, ErrorState, Grade, Field, Loading, MoneyInput, Notice, PageHeader, PriceGuideBar, QtyStepper, Switch, useToast, type PriceGuide } from "../ui/kit";

type SpecEntry = { value: string; source: "ai" | "seller"; confidence?: "high" | "medium" | "low"; confirmed: boolean; note?: string };
type Photo = { id: string; view: string; isBlurry: boolean; url: string; thumbUrl: string };
type Problem = { code: string; field?: string; view?: string; value?: string; message: string };
type Draft = {
  id: string;
  status: string;
  mode: "manual" | "ai";
  category: "phone" | "laptop";
  title: string;
  specs: Record<string, SpecEntry>;
  condition: string | null;
  defects: string;
  accessories: string[];
  description: string;
  priceCents: number | null;
  negotiable: boolean;
  priceGuide: PriceGuide | null;
  currency: string;
  quantityTotal: number;
  city: string;
  pickupAvailable: boolean;
  deliveryAvailable: boolean;
  deliveryFeeCents: number;
  deliveryDays: number;
  photos: Photo[];
  photoSteps: { view: string }[];
  missingViews: { view: string }[];
  questions: { field: string; reason: "missing" | "uncertain" }[];
  ai: { status: string; error: string | null; redactions: number; notes: string[] } | null;
  readyProblems: Problem[];
  imeiLast4?: string | null;
  warranty?: { type: "manufacturer" | "seller"; until: string | null; months: number | null } | null;
};

const REQUIRED: Record<string, string[]> = { phone: ["brand", "model", "storage"], laptop: ["brand", "model", "processor", "ram", "storage"] };
const OPTIONAL: Record<string, string[]> = { phone: ["model_number", "ram", "os_version", "battery_health", "color"], laptop: ["graphics", "os_version", "screen_size", "year"] };
const CONDITIONS = ["new", "like_new", "good", "fair", "for_parts"] as const;
const ACCESSORIES = ["box", "charger", "cable", "case", "receipt", "earphones"] as const;

export function SellFlow() {
  const { id } = useParams();
  const { t } = useI18n();
  const { me, loading: sLoading } = useSession();
  const [params, setParams] = useSearchParams();
  const { data, error, loading, reload, setData } = useAsync(() => api<Draft>(`/api/listings/${id}/edit`), [id]);
  if (!sLoading && !me) return <Navigate to={`/signin?next=/sell/${id}`} replace />;
  if (loading && !data) return <Loading />;
  if (error || !data) return <ErrorState error={error} onRetry={reload} />;
  if (data.status !== "draft" && !params.get("step")) return <Navigate to={`/sell/${id}/manage`} replace />;

  // Three steps: what it is, its condition and price, then photos and publish. A screenshot of the About screen
  // adds a step first; phones are picked from the device library (brand → model → storage → colour) before the
  // specs, while laptops are typed in.
  const steps = [
    ...(data.mode === "ai" && data.status === "draft" ? (["about"] as const) : []),
    ...(data.category === "phone" && data.status === "draft" ? (["device"] as const) : []),
    "specs", "details", "photos",
  ] as const;
  const step = (params.get("step") as (typeof steps)[number]) ?? steps[0];
  const idx = Math.max(0, steps.indexOf(step as never));
  const go = (s: string) => setParams({ step: s });
  const nextStep = () => go(steps[Math.min(idx + 1, steps.length - 1)]);
  const titles: Record<string, MessageKey> = { about: "wiz.about", device: "wiz.device", specs: "wiz.specs", details: "wiz.details", photos: "wiz.photosPublish" };

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <PageHeader title={t(titles[step])} back={idx === 0 ? "/sell" : true} />
      <div className="progress" aria-label={t("wiz.step", { a: idx + 1, b: steps.length })} style={{ marginBottom: "var(--s4)" }}>
        {steps.map((s, i) => <span key={s} className={i < idx ? "done" : i === idx ? "now" : ""} />)}
      </div>
      {step === "about" && <AboutStep draft={data} onDone={(d) => { setData(d); nextStep(); }} onSkip={nextStep} />}
      {step === "device" && <DeviceStep draft={data} onSaved={(d) => { setData(d); go("specs"); }} onManual={() => go("specs")} />}
      {step === "specs" && <SpecsStep draft={data} onSaved={(d) => { setData(d); nextStep(); }} onChangeDevice={steps.includes("device" as never) ? () => go("device") : undefined} />}
      {step === "details" && <DetailsStep draft={data} onSaved={(d) => { setData(d); nextStep(); }} />}
      {step === "photos" && <PhotosStep draft={data} reload={reload} go={go} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
function AboutStep({ draft, onDone, onSkip }: { draft: Draft; onDone: (d: Draft) => void; onSkip: () => void }) {
  const { t } = useI18n();
  const platforms = draft.category === "phone" ? (["iphone", "android"] as const) : (["windows", "mac"] as const);
  const [platform, setPlatform] = useState<(typeof platforms)[number]>(platforms[0]);
  const [phase, setPhase] = useState<"guide" | "reading" | "failed">(draft.ai?.status === "queued" || draft.ai?.status === "processing" ? "reading" : draft.ai?.status === "failed" ? "failed" : "guide");
  const [message, setMessage] = useState<string | null>(draft.ai?.error ?? null);
  const fileRef = useRef<HTMLInputElement>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (phase !== "reading") return;
    let stop = false;
    const poll = async () => {
      while (!stop) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const r = await api<{ ai: Draft["ai"] }>(`/api/listings/${draft.id}/ai`);
          if (r.ai?.status === "done") {
            onDoneRef.current(await api<Draft>(`/api/listings/${draft.id}/edit`));
            return;
          }
          if (r.ai?.status === "failed") {
            setMessage(r.ai.error);
            setPhase("failed");
            return;
          }
        } catch {
          /* keep polling through brief network hiccups */
        }
      }
    };
    poll();
    return () => {
      stop = true;
    };
  }, [phase, draft.id]);

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setMessage(null);
    try {
      await upload(`/api/listings/${draft.id}/about`, f);
      setPhase("reading");
    } catch (e) {
      setMessage((e as ApiError).message);
      setPhase("failed");
    }
  };

  if (phase === "reading")
    return (
      <div className="state" aria-live="polite">
        <span className="spinner" style={{ width: 32, height: 32 }} />
        <h2>{t("about.reading")}</h2>
        <p>{t("about.readingHint")}</p>
      </div>
    );

  return (
    <div className="stack-lg">
      <div className="stack">
        <h2 className="section-title" style={{ marginTop: 0 }}>{t("about.title")}</h2>
        <p className="muted" style={{ margin: 0 }}>{t("about.cantOpen")}</p>
        <div className="chips" role="tablist">
          {platforms.map((p) => (
            <button key={p} role="tab" aria-selected={platform === p} className="chip" aria-pressed={platform === p} onClick={() => setPlatform(p)}>
              {t(`about.${p}`)}
            </button>
          ))}
        </div>
        <div className="card">
          <p style={{ margin: 0 }}>{t(`about.${platform}.steps`)}</p>
          <p className="small muted" style={{ marginBottom: 0 }}>{t("about.photoOk")}</p>
        </div>
        <Notice>{t("about.private")}</Notice>
      </div>
      {phase === "failed" && (
        <Notice kind="bad">
          <strong>{t("about.failed")}</strong>
          <div>{message}</div>
        </Notice>
      )}
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0])} />
      <div className="stack">
        <button className="btn primary block" onClick={() => fileRef.current?.click()}>
          <IconScan size={20} /> {phase === "failed" ? t("about.tryAnother") : t("about.upload")}
        </button>
        <button className="btn ghost block" onClick={onSkip}>{t("about.skip")}</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function SpecsStep({ draft, onSaved, onChangeDevice }: { draft: Draft; onSaved: (d: Draft) => void; onChangeDevice?: () => void }) {
  const { t, locale } = useI18n();
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(Object.entries(draft.specs).map(([k, s]) => [k, s.value])));
  // The device library: suggestions for brand, model and storage, and the colours each model really comes in.
  const devices = useDeviceLibrary(draft.category).data?.brands ?? [];
  const { brandInfo, modelInfo } = findModel(devices, values.brand, values.model);
  // Picked from the library in the previous step: shown as a summary with "Change" rather than as text boxes.
  const picked = !!onChangeDevice && !!modelInfo && modelInfo.storage.includes(storageGb(values.storage) ?? -1);
  const pickedKeys = picked ? ["brand", "model", "storage", "color"] : [];
  const colors = modelInfo?.colors ?? [];
  const pickedColor = colors.find((c) => norm(c.en) === norm(values.color) || norm(c.ar) === norm(values.color));
  const preview = pickedColor?.image ?? modelInfo?.image ?? null;
  const suggestions: Record<string, string[]> = {
    brand: devices.map((b) => b.brand),
    model: brandInfo?.models.map((m) => m.model) ?? [],
    storage: modelInfo?.storage.map(storageLabel) ?? [],
  };
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [imei, setImei] = useState("");
  const [editImei, setEditImei] = useState(!draft.imeiLast4);
  const locked = draft.status !== "draft";
  const required = REQUIRED[draft.category];
  const optional = OPTIONAL[draft.category];

  const needsConfirm = (k: string) => {
    const s = draft.specs[k];
    return s && s.source === "ai" && !s.confirmed && !confirmed[k] && values[k] === s.value && (s.confidence !== "high" || required.includes(k));
  };

  const save = async () => {
    setError(null);
    const specs: Record<string, string | { confirm: true }> = {};
    for (const k of [...required, ...optional]) {
      const v = (values[k] ?? "").trim();
      const s = draft.specs[k];
      if (s && v === s.value) {
        if (s.source === "ai" && !s.confirmed) specs[k] = { confirm: true }; // reviewing the screen and continuing confirms it
      } else if (v || s) specs[k] = v;
    }
    try {
      onSaved(await api<Draft>(`/api/listings/${draft.id}`, { method: "PATCH", body: { specs, ...(imei.trim() ? { imei } : {}) } }));
    } catch (e) {
      setError((e as ApiError).message);
    }
  };

  const colorRow = () => (
    <div key="color" className="spec-row color-row">
      <span className="k" id="spec-color-label">
        {t("spec.color")}
        {values.color && !pickedColor && <div className="tiny" style={{ color: "var(--ink-2)" }}>{t("specs.colorPick")}</div>}
      </span>
      <div className="swatches" role="radiogroup" aria-labelledby="spec-color-label">
        {colors.map((c) => {
          const on = c === pickedColor;
          return (
            <button key={c.en} type="button" role="radio" aria-checked={on} className={`swatch-btn${on ? " on" : ""}`} onClick={() => setValues((v) => ({ ...v, color: on ? "" : c.en }))}>
              <span className="swatch" style={{ background: c.hex }} aria-hidden="true" />
              <span>{locale === "ar" ? c.ar : c.en}</span>
            </button>
          );
        })}
      </div>
      {preview && <img className="device-preview" src={preview} alt={`${values.model} ${pickedColor ? (locale === "ar" ? pickedColor.ar : pickedColor.en) : ""}`.trim()} />}
    </div>
  );

  const row = (k: string) => {
    if (k === "color" && colors.length) return colorRow();
    const s = draft.specs[k];
    const uncertain = needsConfirm(k);
    return (
      <div key={k} className={`spec-row ${uncertain ? "uncertain" : ""}`}>
        <label className="k" htmlFor={`spec-${k}`}>
          {t(`spec.${k}` as MessageKey)}
          {s?.source === "ai" && <span className="src" title={t("spec.fromAbout")} style={{ marginInlineStart: 6, verticalAlign: "middle" }}><IconScan size={14} /></span>}
          {uncertain && <div className="tiny" style={{ color: "var(--ink-2)" }}>{s?.note ?? t("spec.check")}</div>}
        </label>
        <input
          id={`spec-${k}`}
          className="input"
          dir="auto"
          value={values[k] ?? ""}
          disabled={locked && ["brand", "model", "storage"].includes(k)}
          list={suggestions[k]?.length ? `spec-${k}-options` : undefined}
          placeholder={k === "model" ? t(draft.category === "phone" ? "specs.example.model.phone" : "specs.example.model.laptop") : k === "storage" ? t("specs.example.storage") : ""}
          onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
        />
        {uncertain && (
          <button type="button" className="btn sm" onClick={() => setConfirmed((c) => ({ ...c, [k]: true }))} aria-label={`${t("spec.confirm")} ${t(`spec.${k}` as MessageKey)}`}>
            <IconCheck size={16} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="stack-lg">
      {draft.ai?.status === "done" && (
        <div className="stack">
          <Notice kind="good">
            {t("about.done")} {draft.ai.redactions > 0 && t("about.redacted", { n: draft.ai.redactions })}
          </Notice>
          <Notice>{t("about.noInvent")}</Notice>
          {draft.ai.notes.map((n, i) => <Notice key={i} kind="warn">{n}</Notice>)}
        </div>
      )}
      {locked && <Notice>{t("specs.locked")}</Notice>}
      {picked ? (
        <section className="spec-list" aria-label={t("specs.required")}>
          <div className="spec-row">
            <span className="k">
              <strong dir="ltr">{modelInfo!.model}</strong>
              <div className="small muted">
                <span dir="ltr">{values.storage}</span>
                {pickedColor && <> · <span className="swatch" style={{ background: pickedColor.hex }} aria-hidden="true" />{locale === "ar" ? pickedColor.ar : pickedColor.en}</>}
              </div>
            </span>
            <button type="button" className="btn sm" onClick={onChangeDevice}>{t("device.change")}</button>
          </div>
        </section>
      ) : (
        <section className="spec-list" aria-label={t("specs.required")}>
          <div className="head">{t("specs.title")}</div>
          {required.map(row)}
        </section>
      )}
      <section className="spec-list">
        <div className="head">{t("specs.more")} · {t("common.optional")}</div>
        {optional.filter((k) => !pickedKeys.includes(k)).map(row)}
      </section>
      {draft.category === "phone" && (
        <section className="spec-list">
          <div className="spec-row" style={{ flexWrap: "wrap" }}>
            <label className="k" htmlFor="spec-imei">{t("imei.label")} · {t("common.optional")}</label>
            {editImei ? (
              <input id="spec-imei" className="input" inputMode="numeric" dir="ltr" autoComplete="off" value={imei} maxLength={20} onChange={(e) => setImei(e.target.value)} placeholder="35xxxxxxxxxxxxx" aria-describedby="imei-hint" />
            ) : (
              <span className="v">
                {t("imei.saved", { d: draft.imeiLast4 ?? "" })}{" "}
                <button type="button" className="btn ghost sm" onClick={() => setEditImei(true)}>{t("imei.change")}</button>
              </span>
            )}
            <div id="imei-hint" className="tiny muted" style={{ flexBasis: "100%" }}>{t("imei.hint")}</div>
          </div>
        </section>
      )}
      {Object.entries(suggestions).map(([k, opts]) => (
        <datalist key={k} id={`spec-${k}-options`}>
          {opts.map((o) => <option key={o} value={o} />)}
        </datalist>
      ))}
      {error && <Notice kind="bad">{error}</Notice>}
      <div className="sticky-cta">
        <AsyncButton className="btn primary block" onClick={save} disabled={required.some((k) => !(values[k] ?? "").trim())}>
          {t("common.continue")}
        </AsyncButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
/** Condition and price on one screen, with the market price for this exact phone in this condition. */
function DetailsStep({ draft, onSaved }: { draft: Draft; onSaved: (d: Draft) => void }) {
  const { t, money, locale } = useI18n();
  const { me } = useSession();
  const [condition, setCondition] = useState(draft.condition);
  const [defects, setDefects] = useState(draft.defects);
  const [accessories, setAccessories] = useState<string[]>(draft.accessories);
  const [description, setDescription] = useState(draft.description);
  const [price, setPrice] = useState<number | null>(draft.priceCents);
  const [negotiable, setNegotiable] = useState(draft.negotiable);
  const [qty, setQty] = useState(draft.quantityTotal || 1);
  const [city, setCity] = useState(draft.city || cityName(me?.city, locale));
  const [delivery, setDelivery] = useState(draft.deliveryAvailable);
  const [fee, setFee] = useState<number | null>(draft.deliveryFeeCents);
  const [more, setMore] = useState(!!(draft.description || draft.accessories.length || draft.quantityTotal > 1));
  const [guide, setGuide] = useState(draft.priceGuide);
  const [warranty, setWarranty] = useState<"none" | "manufacturer" | "seller">(draft.warranty?.type ?? "none");
  const [until, setUntil] = useState(draft.warranty?.until ?? "");
  const [months, setMonths] = useState(String(draft.warranty?.months ?? 3));
  const [error, setError] = useState<string | null>(null);
  const warrantyBody = warranty === "manufacturer" ? { type: warranty, until } : warranty === "seller" ? { type: warranty, months: Number(months) } : { type: "none" };
  const accLabel = (a: string) => t(`acc.${a}` as MessageKey);
  // The guide depends on the condition: re-read it (by saving the condition) when the seller picks one.
  const pick = async (c: string) => {
    setCondition(c);
    try {
      setGuide((await api<Draft>(`/api/listings/${draft.id}`, { method: "PATCH", body: { condition: c } })).priceGuide);
    } catch {
      /* the guide is a hint; saving happens on continue */
    }
  };
  return (
    <div className="stack-lg">
      <div role="radiogroup" aria-label={t("cond.title")} className="stack">
        <h2 className="section-title" style={{ marginTop: 0 }}>{t("cond.title")}</h2>
        <div className="cond-choices">
          {CONDITIONS.map((c) => (
            <button key={c} type="button" role="radio" aria-checked={condition === c} className="choice" onClick={() => pick(c)} style={{ flexDirection: "row", alignItems: "center", gap: "var(--s3)" }}>
              <Grade c={c} />
              <span style={{ display: "grid", gap: 2, color: "inherit" }}>
                <strong>{t(`cond.${c}`)}</strong>
                <span>{t(`cond.${c}.hint`)}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
      <Field label={t("cond.defects")} hint={t("cond.defectsHint")} optional>
        {(id, d) => <textarea id={id} className="input" value={defects} onChange={(e) => setDefects(e.target.value)} aria-describedby={d} maxLength={1000} dir="auto" rows={2} />}
      </Field>

      <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="label" style={{ marginBottom: 6 }}>{t("warranty.title")}</legend>
        {(["none", "manufacturer", "seller"] as const).map((w) => (
          <label key={w} className="row" style={{ cursor: "pointer", minHeight: 40 }}>
            <input type="radio" name="warranty" checked={warranty === w} onChange={() => setWarranty(w)} style={{ width: 20, height: 20 }} />
            <span>{t(`warranty.${w}`)}</span>
          </label>
        ))}
        {warranty === "manufacturer" && (
          <Field label={t("warranty.until")} hint={t("warranty.manufacturerHint")}>
            {(id, d) => <input id={id} type="date" className="input" value={until} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setUntil(e.target.value)} aria-describedby={d} />}
          </Field>
        )}
        {warranty === "seller" && (
          <Field label={t("warranty.months")}>
            {(id) => (
              <select id={id} className="input" value={months} onChange={(e) => setMonths(e.target.value)}>
                {[1, 3, 6, 12, 24].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
          </Field>
        )}
      </fieldset>

      <div className="stack">
        <Field label={t("price.asking")}>
          {(id, d) => <MoneyInput id={id} value={price} onChange={setPrice} currency={draft.currency} describedBy={d} />}
        </Field>
        {guide && condition ? (
          <>
            <PriceGuideBar guide={guide} priceCents={price} currency={draft.currency} label={t("price.guideFor", { c: t(`cond.${condition}` as MessageKey) })} />
            <div className="row wrap" style={{ gap: 6 }}>
              <button type="button" className="chip" onClick={() => setPrice(guide.lowCents)}>{t("price.sellFast", { p: money(guide.lowCents, draft.currency) })}</button>
              <button type="button" className="chip" onClick={() => setPrice(guide.typicalCents)}>{t("price.useTypical", { p: money(guide.typicalCents, draft.currency) })}</button>
            </div>
          </>
        ) : (
          <span className="tiny muted">{t("price.noGuide")}</span>
        )}
        <span className="small"><IconShield size={14} /> {t("price.free")}</span>
        <div className="toggle-row" style={{ padding: 0 }}>
          <span>{t("price.negotiable")}</span>
          <Switch checked={negotiable} onChange={setNegotiable} label={t("price.negotiable")} />
        </div>
      </div>

      <Field label={t("price.city")}>
        {(id) => <input id={id} className="input" value={city} onChange={(e) => setCity(e.target.value)} autoComplete="address-level2" dir="auto" />}
      </Field>
      <div className="card" style={{ paddingBlock: 4 }}>
        <div className="toggle-row">
          <div className="row"><IconTruck /> <div><div>{t("price.delivery")}</div><div className="small muted">{t("price.deliveryHintDirect")}</div></div></div>
          <Switch checked={delivery} onChange={setDelivery} label={t("price.delivery")} />
        </div>
        {delivery && (
          <div style={{ paddingBottom: "var(--s3)" }}>
            <Field label={t("price.deliveryFee")}>{(id) => <MoneyInput id={id} value={fee} onChange={setFee} currency={draft.currency} />}</Field>
          </div>
        )}
      </div>

      {!more ? (
        <button type="button" className="btn ghost" onClick={() => setMore(true)}>{t("details.more")}</button>
      ) : (
        <>
          <div>
            <div className="label" style={{ marginBottom: 6 }}>{t("cond.accessories")}</div>
            <div className="chips" style={{ flexWrap: "wrap" }}>
              {ACCESSORIES.map((a) => {
                const label = accLabel(a);
                const on = accessories.includes(label);
                return (
                  <button key={a} type="button" className="chip" aria-pressed={on} onClick={() => setAccessories((x) => (on ? x.filter((y) => y !== label) : [...x, label]))}>
                    {on && <IconCheck size={14} />} {label}
                  </button>
                );
              })}
            </div>
          </div>
          <Field label={t("cond.description")} optional>
            {(id) => <textarea id={id} className="input" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} dir="auto" />}
          </Field>
          <div className="row between">
            <span className="label">{t("price.quantity")}</span>
            <QtyStepper value={qty} max={10_000} onChange={setQty} label={t("price.quantity")} />
          </div>
        </>
      )}
      {error && <Notice kind="bad">{error}</Notice>}
      <div className="sticky-cta">
        <AsyncButton
          className="btn primary block"
          disabled={!condition || !price || !city.trim() || (warranty === "manufacturer" && !until)}
          onClick={async () => {
            setError(null);
            try {
              onSaved(
                await api<Draft>(`/api/listings/${draft.id}`, {
                  method: "PATCH",
                  body: {
                    condition, defects, accessories, description, warranty: warrantyBody, priceCents: price, negotiable, quantity: qty, city: city.trim(),
                    pickupAvailable: true, deliveryAvailable: delivery, deliveryFeeCents: delivery ? fee ?? 0 : 0,
                  },
                }),
              );
            } catch (e) {
              setError((e as ApiError).message);
            }
          }}
        >
          {t("common.continue")}
        </AsyncButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function PhotosStep({ draft, reload, go }: { draft: Draft; reload: () => Promise<void>; go: (s: string) => void }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const toast = useToast();
  const [agreed, setAgreed] = useState(false);
  const [published, setPublished] = useState(false);
  const isDraft = draft.status === "draft";
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blurry, setBlurry] = useState<Photo | null>(null);
  const [extraView, setExtraView] = useState<string | null>(null);
  const missing = draft.missingViews;
  const have = new Set(draft.photos.map((p) => p.view));
  // Required views first; then the suggested ones, which the seller can skip.
  const suggested = draft.photoSteps.filter((s) => s.view !== "damage" && !have.has(s.view) && !missing.some((m) => m.view === s.view));
  const current = extraView ?? missing[0]?.view ?? null;
  const required = draft.photoSteps.filter((s) => missing.some((m) => m.view === s.view) || (have.has(s.view) && !suggested.some((x) => x.view === s.view)));
  const done = required.filter((s) => !missing.some((m) => m.view === s.view)).length;
  const otherProblems = draft.readyProblems.filter((p) => p.code !== "photo_missing");
  const problemText = (p: Problem) => {
    const f = p.field ? t(`spec.${p.field}` as MessageKey) : "";
    const v = p.view ? t(`view.${p.view}` as MessageKey) : (p.value ?? "");
    return t(`ready.${p.code}` as MessageKey, { f: f.toLowerCase(), v });
  };
  const stepFor = (p: Problem) => (p.code.startsWith("spec") ? "specs" : "details");

  if (published)
    return (
      <div className="state">
        <IconCheck size={40} />
        <h2>{t("preview.published")}</h2>
        <p>{t("preview.publishedBody")}</p>
        <Link className="btn primary" to={`/l/${draft.id}`}>{t("preview.view")}</Link>
        <Link className="btn" to={`/sell/${draft.id}/manage`}>{t("listing.manage")}</Link>
      </div>
    );

  const onFile = async (f: File | undefined) => {
    if (!f || !current) return;
    setBusy(true);
    setError(null);
    try {
      const r = await upload<{ photo: Photo; blurry: boolean }>(`/api/listings/${draft.id}/photos?view=${current}`, f);
      setBlurry(r.blurry ? r.photo : null);
      setExtraView(null);
      await reload();
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="stack-lg">
      <div className="progress" aria-label={`${done}/${required.length}`}>
        {required.map((s) => <span key={s.view} className={missing.some((m) => m.view === s.view) ? (s.view === current ? "now" : "") : "done"} />)}
      </div>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => onFile(e.target.files?.[0])} />

      {blurry && (
        <Notice kind="warn">
          {t("photos.blurry")}
          <div className="row" style={{ marginTop: 8 }}>
            <button className="btn sm" onClick={() => { setExtraView(blurry.view); setBlurry(null); fileRef.current?.click(); }}>{t("photos.retake")}</button>
            <AsyncButton className="btn sm ghost" onClick={async () => { await api(`/api/media/${blurry.id}`, { method: "PATCH", body: { keepAnyway: true } }); setBlurry(null); await reload(); }}>
              {t("photos.keep")}
            </AsyncButton>
          </div>
        </Notice>
      )}

      {current ? (
        <div className="shot-guide">
          <div className="row between">
            <h2 className="section-title" style={{ margin: 0 }}>{t(`view.${current}` as MessageKey)}</h2>
            <span className="small muted num">{done}/{required.length}</span>
          </div>
          {current === "damage" && <Notice kind="warn">{t("photos.damageHint")}</Notice>}
          <button className="shot-frame" onClick={() => fileRef.current?.click()} disabled={busy} style={{ cursor: "pointer" }}>
            <div className="stack" style={{ justifyItems: "center" }}>
              {busy ? <span className="spinner" /> : <IconCamera size={36} />}
              <div style={{ fontWeight: 600 }}>{busy ? t("photos.uploading") : t("photos.take")}</div>
              <div className="small muted">{t(`hint.${current}` as MessageKey)}</div>
            </div>
          </button>
          <div className="tiny muted">{t("photos.tips")}</div>
        </div>
      ) : (
        <Notice kind="good">{t("photos.allDone")}</Notice>
      )}
      {error && <Notice kind="bad">{error}</Notice>}

      {draft.photos.length > 0 && (
        <div className="thumbs">
          {draft.photos.map((p) => (
            <figure key={p.id}>
              <img src={p.thumbUrl} alt={t(`view.${p.view}` as MessageKey)} />
              {p.isBlurry && <span className="flag tag bad"><IconAlert size={12} /></span>}
              <figcaption className="row between">
                <span>{t(`view.${p.view}` as MessageKey)}</span>
                <AsyncButton className="icon-btn" onClick={async () => { await api(`/api/media/${p.id}`, { method: "DELETE" }); await reload(); }}>
                  <IconX size={14} />
                </AsyncButton>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {!current && suggested.length > 0 && (
        <div className="stack">
          <span className="small muted">{t("photos.suggested")}</span>
          <div className="chips" style={{ flexWrap: "wrap" }}>
            {suggested.map((s) => (
              <button key={s.view} type="button" className="chip" onClick={() => { setExtraView(s.view); setTimeout(() => fileRef.current?.click(), 0); }}>
                <IconCamera size={14} /> {t(`view.${s.view}` as MessageKey)}
              </button>
            ))}
          </div>
        </div>
      )}
      {!current && !suggested.length && (
        <button className="btn ghost" onClick={() => { setExtraView("other"); setTimeout(() => fileRef.current?.click(), 0); }}>
          {t("photos.addMore")}
        </button>
      )}
      {isDraft && !missing.length && (
        otherProblems.length > 0 ? (
          <section className="card stack">
            <strong>{t("preview.missing")}</strong>
            {otherProblems.map((p, i) => (
              <button key={i} className="btn sm ghost" style={{ justifyContent: "flex-start", color: "var(--ink)" }} onClick={() => go(stepFor(p))}>
                <IconAlert size={16} /> {problemText(p)}
              </button>
            ))}
          </section>
        ) : (
          <label className="row card" style={{ alignItems: "flex-start", cursor: "pointer" }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ width: 22, height: 22, marginTop: 2 }} />
            <span>{t("preview.confirm")}</span>
          </label>
        )
      )}
      <div className="sticky-cta stack">
        {isDraft ? (
          missing.length ? (
            <button className="btn primary block" disabled>{t("photos.next", { v: t(`view.${missing[0].view}` as MessageKey) })}</button>
          ) : (
            <AsyncButton
              className="btn primary block"
              disabled={!agreed || otherProblems.length > 0}
              busyLabel={t("preview.publishing")}
              onClick={async () => {
                setError(null);
                try {
                  await api(`/api/listings/${draft.id}/publish`, { body: { confirm: true } });
                  setPublished(true);
                  toast(t("preview.published"));
                } catch (e) {
                  setError((e as ApiError).message);
                }
              }}
            >
              {t("preview.publish")}
            </AsyncButton>
          )
        ) : (
          <button className="btn primary block" onClick={() => nav(`/sell/${draft.id}/manage`)}>{t("common.done")}</button>
        )}
        {isDraft && <button className="btn ghost block" onClick={() => nav("/sell")}>{t("preview.saveDraft")}</button>}
      </div>
    </div>
  );
}

